/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Sujith Dachepally
 * Created: 22/04/24
 */

import React, { useCallback, useRef, useState } from 'react';
import {
    Alert,
    AlertIcon,
    Box,
    Flex,
    Grid,
    IconButton,
    Input,
    InputGroup,
    InputRightAddon,
    Link,
    List,
    ListItem,
    Select,
    Text,
    UnorderedList,
} from '@chakra-ui/react';
import { ErrorCode, FileError, FileRejection, useDropzone } from 'react-dropzone';
import { useDeleteRequest, useGetRequest, usePatchRequest } from '@/utils/useStatefulRequests';
import {
    mapWorkplanAttachmentTypeToString,
    WorkplanAttachment,
    WorkplanAttachmentType,
} from '@/wpt-lib/interfaces/Workplan';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { LoadingSpinner } from '@/components';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { AxiosRequestConfig } from 'axios';
import { Dialog } from '@/components/Dialog';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import Icons from '@/components/icons/Icons';

interface FileUploadProps {
    onDrop: (files: File[]) => void;
    isReadonly: boolean;
    getListOfWorkplanAttachments: () => Promise<void>;
    currentAssignment: Assignment;
    baseApiUrl: string;
    existingFiles: WorkplanAttachment[];
    uploadLoading: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
    onDrop,
    isReadonly,
    getListOfWorkplanAttachments,
    baseApiUrl,
    currentAssignment,
    existingFiles,
    uploadLoading,
}) => {
    const { admin } = useNavigateOptions();
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
    const [editIndex, setEditIndex] = useState<number | undefined>(undefined);
    const [editName, setEditName] = useState<string>('');
    const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
    const [deleteFile, setDeleteFile] = useState<WorkplanAttachment | undefined>(undefined);

    const {
        makeRequest: makeDeleteRequest,
        error: deleteFileError,
        loading: deleteFileLoading,
    } = useDeleteRequest<WorkplanAttachment>();

    const { makeRequest, error } = useGetRequest<Blob>({ noCache: true });

    const { makeRequest: makeEditRequest, error: updateError } = usePatchRequest<
        WorkplanAttachment,
        { fileName?: string; attachmentType?: WorkplanAttachmentType }
    >();

    const downloadWorkplanAttachment = async (workplanAttachment: WorkplanAttachment) => {
        try {
            const config: AxiosRequestConfig = {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'application/pdf',
                },
                params: { assignment: currentAssignment?.id },
            };
            const response = await makeRequest(`${baseApiUrl}/file/${workplanAttachment.id}/download`, config);

            if (response && response.data) {
                const url = URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = workplanAttachment.fileName;
                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                }, 500);
            }
        } catch (e) {
            //handled by hook
        }
    };

    const deleteWorkplanAttachment = async (workplanAttachment: WorkplanAttachment): Promise<void> => {
        try {
            await makeDeleteRequest(`${baseApiUrl}/file/${workplanAttachment.id}`, {
                params: {
                    assignment: currentAssignment?.id,
                    forceDelete: admin,
                },
            });
            await getListOfWorkplanAttachments();
            setDeleteFile(undefined);
        } catch (e) {
            //handled by hook
        }
    };

    const updateFileName = useCallback(
        async (newFileName: string, workplanAttachment: WorkplanAttachment) => {
            try {
                await makeEditRequest(
                    `/${baseApiUrl}/file/${workplanAttachment.id}`,
                    { fileName: newFileName },
                    { params: { assignment: currentAssignment?.id } },
                );
                await getListOfWorkplanAttachments();
            } catch (error) {
                //handled by hook
            }
        },
        [baseApiUrl, currentAssignment],
    );

    const updateAttachmentType = useCallback(
        async (newAttachmentType: WorkplanAttachmentType, workplanAttachment: WorkplanAttachment) => {
            try {
                await makeEditRequest(
                    `/${baseApiUrl}/file/${workplanAttachment.id}`,
                    { attachmentType: newAttachmentType },
                    { params: { assignment: currentAssignment?.id } },
                );
                await getListOfWorkplanAttachments();
            } catch (error) {
                //handled by hook
            }
        },
        [baseApiUrl, currentAssignment],
    );

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        accept: {
            'application/pdf': [],
        },
        maxSize: 10 * 1024 * 1024, // 10MB
        maxFiles: 10 - existingFiles.length,
        onDrop: (acceptedFiles: File[]) => {
            setEditIndex(undefined);
            const uniqueFiles = acceptedFiles.filter(
                (file) =>
                    !existingFiles.some(
                        (existingFile) => existingFile.fileName === file.name && existingFile.fileSize === file.size,
                    ),
            );
            const duplicates = acceptedFiles.filter((file) =>
                existingFiles.some(
                    (existingFile) => existingFile.fileName === file.name && existingFile.fileSize === file.size,
                ),
            );

            const filesToUpload = uniqueFiles.slice(0, 10 - existingFiles.length);
            if (filesToUpload.length > 0) {
                onDrop(filesToUpload); // Upload unique files on drop, limited to max 10 files
            }
            setDuplicateFiles(duplicates);
        },
        validator: () =>
            existingFiles.length + 1 <= 10
                ? null
                : {
                      code: ErrorCode.FileInvalidType,
                      message: `Too many files${
                          10 - existingFiles.length > 0
                              ? ', only ' + (10 - existingFiles.length) + ' more files may be uploaded.'
                              : ''
                      } `,
                  },
    });

    const handleEditStart = (file: WorkplanAttachment, index: number) => {
        setEditIndex(index);
        setEditName(file.fileName.split('.').slice(0, -1).join('.')); // removing .pdf
        setTimeout(() => {
            inputRefs.current[index]?.focus();
        }, 0);
    };

    const handleEditSave = async (workplanAttachment: WorkplanAttachment) => {
        if (workplanAttachment.fileName !== `${editName}.pdf`) {
            await updateFileName(`${editName}.pdf`, workplanAttachment);
        }
        setEditIndex(undefined);
        setEditName('');
    };

    const handleEditStop = () => {
        setEditIndex(undefined);
        setEditName('');
    };

    const handleDownload = (file: WorkplanAttachment) => {
        downloadWorkplanAttachment(file);
    };

    const handleDocumentTypeChange = async (
        workplanAttachment: WorkplanAttachment,
        attachmentType: WorkplanAttachmentType,
    ) => {
        await updateAttachmentType(attachmentType, workplanAttachment);
    };

    return (
        <Box>
            {!!updateError && (
                <SimpleAlert variant={'left-accent'}>
                    <Text>Could not edit the name of the file, please refresh the page and try again</Text>
                </SimpleAlert>
            )}
            {!!error && (
                <SimpleAlert variant={'left-accent'}>
                    <Text>Could not download the file, please refresh the page and try again</Text>
                </SimpleAlert>
            )}
            {!isReadonly && (
                <Flex
                    {...getRootProps()}
                    borderWidth={1}
                    h="100px"
                    borderStyle="dashed"
                    borderColor="gray.300"
                    borderRadius="md"
                    alignItems="center"
                    justifyContent="center"
                    p={4}
                    textAlign="center"
                    cursor="pointer"
                    w="lg"
                    maxWidth="lg"
                    aria-label={`Dropzone for uploading PDF files (${10 - existingFiles.length} files remaining)`}
                    as="button"
                    _focus={{
                        boxShadow: 'none',
                        outlineStyle: 'solid',
                        outlineColor: 'black',
                        outlineWidth: 3,
                    }}
                    bg={isDragActive ? 'gray.100' : 'white'}
                >
                    <input {...getInputProps()} aria-labelledby="dropzone-label" />
                    <Box id="dropzone-label" w="full">
                        {isDragActive ? (
                            <Text>Drop the files here...</Text>
                        ) : (
                            <>
                                {existingFiles.length >= 10 ? (
                                    <Alert status="warning" sx={{ textAlign: 'left' }}>
                                        <AlertIcon />
                                        Maximum number of files reached, no more files may be uploaded until another
                                        file is removed.
                                    </Alert>
                                ) : (
                                    <Text>
                                        Drag and drop PDF files here, or click to select files
                                        <br />({10 - existingFiles.length} {existingFiles.length < 9 ? 'files' : 'file'}{' '}
                                        remaining)
                                    </Text>
                                )}
                            </>
                        )}
                    </Box>
                </Flex>
            )}
            {isReadonly && existingFiles.length === 0 && (
                <Text color={'gray.700'} fontStyle="italic" mt={3}>
                    No supporting documents have been uploaded
                </Text>
            )}
            {fileRejections.length > 0 && (
                <Box mt={4} aria-label="Rejected Files">
                    {fileRejections.map(({ file, errors }: FileRejection) => (
                        <Box key={file.name} mb={4}>
                            <Text fontWeight="bold" color="red.500">
                                {file.name}
                            </Text>
                            <UnorderedList>
                                {errors.map((error) => (
                                    <ListItem key={error.code} color="red.500">
                                        {formatFileError(error)}
                                    </ListItem>
                                ))}
                            </UnorderedList>
                        </Box>
                    ))}
                </Box>
            )}
            {duplicateFiles.length > 0 && (
                <Box mt={4}>
                    <Text color="red.500">The following files already exist and were not uploaded:</Text>
                    {duplicateFiles.map((file, index) => (
                        <UnorderedList role="list" key={index}>
                            <ListItem key={file.name} color="red.500" aria-label={`File: ${file.name}`}>
                                {file.name}
                            </ListItem>
                        </UnorderedList>
                    ))}
                </Box>
            )}
            <Box mt={4}>
                {uploadLoading && <LoadingSpinner />}
                <List role="list">
                    {existingFiles.map((file, index) => (
                        <ListItem key={index}>
                            <Grid templateColumns="350px 80px 360px 40px 40px" alignItems="center" gap={2}>
                                {editIndex === index ? (
                                    <InputGroup key={`input-${index}`}>
                                        <Input
                                            ref={(el) => (inputRefs.current[index] = el)}
                                            type="text"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleEditSave(file);
                                                }
                                                if (e.key === 'Escape') {
                                                    handleEditStop();
                                                }
                                            }}
                                            onBlur={() => handleEditSave(file)}
                                        />
                                        <InputRightAddon>.pdf</InputRightAddon>
                                    </InputGroup>
                                ) : (
                                    <Link
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDownload(file);
                                        }}
                                        download={file.fileName}
                                        aria-label={`Download ${file.fileName}`}
                                        color="blue.500"
                                        tabIndex={0}
                                        maxWidth="100%"
                                    >
                                        {file.fileName}
                                    </Link>
                                )}
                                <Text
                                    aria-label={`File size: ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                                    _focus={{
                                        outlineColor: 'blue.500',
                                    }}
                                    tabIndex={0}
                                >
                                    {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                                </Text>
                                {isReadonly ? (
                                    <Text
                                        aria-label={`Support document type: ${
                                            mapWorkplanAttachmentTypeToString[file.attachmentType]
                                        }`}
                                        bg="gray.100"
                                        textAlign="center"
                                        py={2}
                                        borderRadius="100px"
                                        tabIndex={0}
                                        _focus={{
                                            outlineColor: 'blue.500',
                                        }}
                                    >
                                        {mapWorkplanAttachmentTypeToString[file.attachmentType]}
                                    </Text>
                                ) : (
                                    <Select
                                        value={file.attachmentType}
                                        isReadOnly={isReadonly}
                                        onChange={(e) =>
                                            handleDocumentTypeChange(file, e.target.value as WorkplanAttachmentType)
                                        }
                                        tabIndex={0}
                                        role="combobox"
                                        aria-label="Supporting document type"
                                    >
                                        {Object.keys(mapWorkplanAttachmentTypeToString).map((type) => (
                                            <option
                                                key={type}
                                                value={type as WorkplanAttachmentType}
                                                disabled={isReadonly}
                                            >
                                                {mapWorkplanAttachmentTypeToString[type as WorkplanAttachmentType]}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                                <IconButton
                                    variant="solid"
                                    aria-label={`Edit ${file.fileName} name`}
                                    fontSize="15px"
                                    icon={<Icons.EditIcon />}
                                    onClick={() => {
                                        !editName && !editIndex ? handleEditStart(file, index) : handleEditStop();
                                    }}
                                    disabled={isReadonly}
                                    tabIndex={0}
                                />
                                <IconButton
                                    variant="solid"
                                    aria-label={`Delete ${file.fileName}`}
                                    fontSize="15px"
                                    icon={<Icons.DeleteIcon />}
                                    onClick={() => setDeleteFile(file)}
                                    disabled={isReadonly && !admin}
                                    tabIndex={0}
                                />
                            </Grid>
                        </ListItem>
                    ))}
                </List>
            </Box>
            <Dialog
                colorScheme="red"
                isOpen={!!deleteFile}
                isLoading={deleteFileLoading}
                dialogHeader={`Delete ${deleteFile?.fileName}`}
                dialogConfirmLabel="Delete"
                onClose={() => setDeleteFile(undefined)}
                errorMsg={
                    deleteFileError ? 'Could not delete the file, please refresh the page and try again' : undefined
                }
                dialogAction={async () => {
                    if (deleteFile) await deleteWorkplanAttachment(deleteFile);
                }}
            >
                <Text>Are you sure you want to delete this file? You can&apos;t undo this action afterwards.</Text>
            </Dialog>
        </Box>
    );
};

export default FileUpload;

function formatFileError(error: FileError): string {
    if (error.code === ErrorCode.FileInvalidType && error.message?.indexOf('application/pdf') >= 0)
        return 'File type must be PDF';
    else if (error.code === ErrorCode.FileTooLarge) return 'File is larger than 10 MB';
    else return error.message;
}
